// Destinos do painel (DESIGN §5.19, §7.3 e §9.0 rodada 2). Itens sem a permissão NÃO aparecem (sidebar,
// bottom nav e "Mais"). Permissões e pacotes padrão: docs/seguranca/RBAC.md. A API confere de novo (403).
import type { Permissao } from "@grindset/server/contratos";
import { Icones, type TipoIcone } from "../compartilhado/ui";
import { IconesPainel } from "./comum/icones";

export interface DestinoPainel {
  para: string;
  rotulo: string;
  icone: TipoIcone;
  /** Precisa de TODAS (vazio = qualquer pessoa da equipe). Telas de gestão pedem a de ver e a de gerir. */
  permissoes?: readonly Permissao[];
  /** Basta UMA destas (tela com abas de permissões diferentes, ex.: Carteira: dividir ou decidir pedidos). */
  qualquer?: readonly Permissao[];
  exato?: boolean;
  contador?: "mensagens" | "avisos" | "pedidosTransferencia";
}

export interface GrupoPainel {
  rotulo: string;
  itens: DestinoPainel[];
}

export const GRUPOS_PAINEL: GrupoPainel[] = [
  {
    rotulo: "Operação",
    itens: [
      {
        para: "/painel",
        rotulo: "Visão geral",
        icone: Icones.visaoGeral,
        exato: true,
        permissoes: ["visao-geral.ver"],
      },
      { para: "/painel/alunos", rotulo: "Alunos", icone: Icones.alunos, permissoes: ["alunos.buscar"] },
      {
        para: "/painel/carteira",
        rotulo: "Carteira de alunos",
        icone: IconesPainel.carteira,
        // Aba "Dividir carteira" com `alunos.transferir`; "Pedidos" e "Histórico" com `alunos.aprovar-transferencia`.
        qualquer: ["alunos.transferir", "alunos.aprovar-transferencia"],
        contador: "pedidosTransferencia",
      },
      {
        para: "/painel/checkin",
        rotulo: "Check-in",
        icone: Icones.checkin,
        permissoes: ["checkin.registrar"],
      },
      {
        para: "/painel/treinos",
        rotulo: "Treinos",
        icone: Icones.exercicios,
        permissoes: ["treino.prescrever"],
      },
      {
        para: "/painel/mensagens",
        rotulo: "Mensagens",
        icone: Icones.mensagens,
        contador: "mensagens",
        permissoes: ["mensagens.usar"],
      },
      { para: "/painel/avisos", rotulo: "Avisos", icone: Icones.avisos, contador: "avisos" },
    ],
  },
  {
    rotulo: "Engajamento",
    itens: [
      {
        para: "/painel/timeline",
        rotulo: "Timeline",
        icone: IconesPainel.timeline,
        permissoes: ["comunidade.publicar"],
      },
      {
        para: "/painel/eventos",
        rotulo: "Eventos",
        icone: IconesPainel.eventos,
        permissoes: ["eventos.operar"],
      },
      {
        para: "/painel/desafios",
        rotulo: "Desafios e sorteios",
        icone: Icones.desafios,
        permissoes: ["desafios.ver"],
      },
      { para: "/painel/premios", rotulo: "Prêmios", icone: Icones.premios, permissoes: ["premios.entregar"] },
      {
        para: "/painel/automacoes",
        rotulo: "Automações",
        icone: Icones.automacoes,
        permissoes: ["automacoes.ver", "automacoes.gerir"],
      },
    ],
  },
  {
    rotulo: "Comunidade",
    itens: [
      {
        para: "/painel/pontos-extras",
        rotulo: "Pontos extras",
        icone: IconesPainel.pontosExtras,
        permissoes: ["pontos-extras.avaliar"],
      },
      {
        para: "/painel/avaliacoes",
        rotulo: "Avaliações físicas",
        icone: IconesPainel.avaliacoes,
        permissoes: ["avaliacoes.registrar"],
      },
      {
        para: "/painel/feedback",
        rotulo: "Feedback",
        icone: IconesPainel.feedback,
        permissoes: ["feedback.ver", "feedback.responder"],
      },
      {
        para: "/painel/indicacoes",
        rotulo: "Indicações",
        icone: IconesPainel.indicacoes,
        permissoes: ["indicacoes.registrar", "indicacoes.gerir"],
      },
    ],
  },
  {
    rotulo: "Configuração",
    itens: [
      {
        para: "/painel/config/pontos",
        rotulo: "Pontos e bônus",
        icone: Icones.regras,
        permissoes: ["gamificacao.ver"],
      },
      {
        para: "/painel/config/gamificacao",
        rotulo: "Gamificação",
        icone: Icones.conquista,
        permissoes: ["gamificacao.ver"],
      },
      {
        para: "/painel/config/unidade",
        rotulo: "Unidade",
        icone: Icones.configuracoes,
        permissoes: ["configuracoes.gerir"],
      },
      { para: "/painel/config/equipe", rotulo: "Equipe", icone: Icones.equipe, permissoes: ["equipe.gerir"] },
      {
        para: "/painel/config/permissoes",
        rotulo: "Permissões",
        icone: IconesPainel.permissoes,
        permissoes: ["permissoes.gerir"],
      },
      {
        para: "/painel/config/integracao",
        rotulo: "Integração",
        icone: Icones.integracao,
        permissoes: ["integracao.gerir"],
      },
    ],
  },
  {
    rotulo: "Resultados",
    itens: [
      {
        para: "/painel/resultados",
        rotulo: "Resultados",
        icone: Icones.resultados,
        permissoes: ["resultados.ver", "resultados.exportar"],
      },
      {
        para: "/painel/suspeitas",
        rotulo: "Suspeitas",
        icone: IconesPainel.suspeitas,
        permissoes: ["suspeitas.ver"],
      },
      {
        para: "/painel/config/auditoria",
        rotulo: "Auditoria",
        icone: IconesPainel.auditoria,
        permissoes: ["auditoria.ver"],
      },
    ],
  },
];

/** Bottom nav (< lg): 4 destinos fixos + "Mais". */
export const BOTTOM_NAV_PAINEL: DestinoPainel[] = [
  {
    para: "/painel",
    rotulo: "Visão geral",
    icone: Icones.visaoGeral,
    exato: true,
    permissoes: ["visao-geral.ver"],
  },
  { para: "/painel/alunos", rotulo: "Alunos", icone: Icones.alunos, permissoes: ["alunos.buscar"] },
  { para: "/painel/checkin", rotulo: "Check-in", icone: Icones.checkin, permissoes: ["checkin.registrar"] },
  {
    para: "/painel/mensagens",
    rotulo: "Mensagens",
    icone: Icones.mensagens,
    contador: "mensagens",
    permissoes: ["mensagens.usar"],
  },
];

type Pode = (p: Permissao) => boolean;

export const destinoVisivel = (d: DestinoPainel, pode: Pode): boolean =>
  (d.permissoes ?? []).every(pode) && (!d.qualquer || d.qualquer.some(pode));

export function gruposVisiveis(pode: Pode): GrupoPainel[] {
  return GRUPOS_PAINEL.map((g) => ({
    ...g,
    itens: g.itens.filter((i) => destinoVisivel(i, pode)),
  })).filter((g) => g.itens.length > 0);
}

/**
 * Primeira tela permitida pelo menu (início de quem não vê a visão geral, ex.: recepção). `null` se o menu
 * não tiver nenhuma (a tela diz "Você ainda não tem acesso a nenhuma tela").
 */
export function primeiroDestino(pode: Pode): string | null {
  for (const g of gruposVisiveis(pode)) for (const i of g.itens) if (i.para !== "/painel") return i.para;
  return null;
}

// Rótulo e ordem dos papéis moram em comum/rotulos.ts (puro, testado em caixa-texto.test.ts).
export { ORDEM_PAPEIS, ROTULO_PAPEL } from "./comum/rotulos";
